import { GetPastModificationsRequest, ModificationRequest, RequestClass } from 'acscp/api/requests';
import React from 'react';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { ButtonWithLoadingIndicator } from '../common/buttonWithLoadingIndicator';
import { Dispatch } from 'redux';
import { AppState, BookingModificationRequest } from 'acscp/store';
import { FetchRequest, FetchResetStatus } from 'acscp/actions';
import { defineMessages, FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import { RequestStateProps } from '../common/helpers/requestStateProps';
import style from './modificationRequest.module.scss';
import { FormValidator } from '../common/helpers/formValidator';
import { StringValidationChecks } from '../common/helpers/validationChecks';
import { RequestErrorMessage } from '../common/requestErrorMessage';
import { ErrorsDisplay } from '../common/errorsDisplay';
import { PreviousModificationRequests } from './previousModificationRequests';
import { LoadingIndicator } from '../common/loadingIndicator';
import { History } from 'history';
import { RouteComponentProps, withRouter } from 'react-router-dom';

const messages = defineMessages({
    requestMissing: {
        id: 'components.booking.modificationRequestPage.requestMissing',
        defaultMessage: 'Please enter a modification request.'
    },
    exceededMaxNumberOfChars: {
        id: 'components.booking.modificationRequestPage.exceededMaxNumberOfChars',
        defaultMessage: 'You have exceeded the maximum number of characters.',
    }
});

interface ModificationRequestPageOwnProps {
    bookingId: string;
    closeModal: (history: History) => void;
}

interface ModificationRequestPageStateProps {
    getPastModificationsRequestState: RequestStateProps;
    submitModificationRequestState: RequestStateProps;
    pastModificationRequests: BookingModificationRequest[];
}

interface ModificationRequestPageDispatchProps {
    fetchPastModificationRequests: () => void;
    submitModificationRequest: (request: string) => void;
    resetRequestErrors: () => void;
}

interface ModificationRequestPageForm {
    modificationRequestValue: string;
}

type ModificationRequestPageState = ModificationRequestPageForm & {
    showErrors: boolean;
};

type ModificationRequestPageProps = InjectedIntlProps
    & RouteComponentProps<any>
    & ModificationRequestPageOwnProps
    & ModificationRequestPageStateProps
    & ModificationRequestPageDispatchProps;

export class ModificationRequestPagePresentational
    extends React.Component<ModificationRequestPageProps, ModificationRequestPageState> {

    private validator: FormValidator;

    constructor(props: ModificationRequestPageProps) {
        super(props);

        const translator = this.props.intl.formatMessage;

        this.validator = new FormValidator([
            {
                validatedPropertyName: 'modificationRequestValue',
                validationCheck: StringValidationChecks.isRequired(() => this.state.modificationRequestValue),
                errorMessage: translator(messages.requestMissing)
            },
            {
                validatedPropertyName: 'modificationRequestValue',
                validationCheck: StringValidationChecks.stringLengthIsLessThanCharLimit(() => this.state.modificationRequestValue, 300),
                errorMessage: translator(messages.exceededMaxNumberOfChars)
            },
        ]);

        this.state = { modificationRequestValue: '', showErrors: false };
    }

    componentDidMount(): void {
        this.props.fetchPastModificationRequests();
        this.props.resetRequestErrors();
    }

    componentDidUpdate(): void {
        if (this.props.submitModificationRequestState.success) {
            this.props.closeModal(this.props.history);
        }
    }

    private handleModificationRequest = () => {
        if (!this.props.submitModificationRequestState.isLoading) {
            this.props.resetRequestErrors();
            if (this.validator.isStateValid()) {
                this.props.submitModificationRequest(this.state.modificationRequestValue);
            }
            this.setState({ showErrors: true });
        }
    }

    private handleModificationRequestChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ modificationRequestValue: event.target.value });
    }

    render(): JSX.Element {
        const validationErrors = this.validator.getDisplayedErrors(
            this.state.showErrors,
            this.props.submitModificationRequestState.requestError);
        const errorMessages = (validationErrors.modificationRequestValue || []).map(err => err.message);

        return (
            <div className={style.ModificationRequestPage}>
                <FormattedMessage
                    id={'components.booking.modificationRequestPage.modificationRequest'}
                    defaultMessage={'Please detail the changes that are required.'}
                >
                    {(msg: string) => (
                        <textarea
                            className={style.RequestInput}
                            onChange={this.handleModificationRequestChange}
                            placeholder={msg}
                            maxLength={3000}
                            rows={10}
                        />
                    )}
                </FormattedMessage>
                <ErrorsDisplay errors={errorMessages} showUpArrow={true} />
                <ButtonWithLoadingIndicator
                    className={style.Button}
                    isLoading={this.props.submitModificationRequestState.isLoading}
                    onClick={this.handleModificationRequest}
                >
                    <FormattedMessage
                        id={'components.booking.modificationRequestPage.submitButton'}
                        defaultMessage={'SUBMIT'}
                    />
                </ButtonWithLoadingIndicator>
                <RequestErrorMessage requestState={this.props.submitModificationRequestState} />
                {this.props.getPastModificationsRequestState.isLoading
                    ? <LoadingIndicator />
                    : this.props.pastModificationRequests.length > 0
                    && <PreviousModificationRequests modificationRequests={this.props.pastModificationRequests} />
                }
            </div>
        );
    }
}

function mapStateToProps(
    state: AppState,
    ownProps: ModificationRequestPageOwnProps, getRequestError: (request: RequestClass<any>) => RequestStateProps
): ModificationRequestPageStateProps {
    const getPastModificationsRequestState = getRequestError(GetPastModificationsRequest);
    const submitModificationRequestState = getRequestError(ModificationRequest);
    const pastModificationRequests = state.bookingModificationRequests.hasOwnProperty(ownProps.bookingId)
        ? state.bookingModificationRequests[ownProps.bookingId]
        : [];

    return { getPastModificationsRequestState, submitModificationRequestState, pastModificationRequests };
}

function mapDispatchToProps(
    dispatch: Dispatch,
    ownProps: ModificationRequestPageOwnProps): ModificationRequestPageDispatchProps {
    return {
        fetchPastModificationRequests: () => dispatch(new FetchRequest(new GetPastModificationsRequest(ownProps.bookingId))),
        submitModificationRequest:
            (request: string) => dispatch(new FetchRequest(new ModificationRequest(ownProps.bookingId, request))),
        resetRequestErrors: () => dispatch(new FetchResetStatus(ModificationRequest))
    };
}

export const ModificationRequestPage =
    connectWithRequests(mapStateToProps, mapDispatchToProps)(withRouter(injectIntl(ModificationRequestPagePresentational)));
